import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ScanStationPage from '../../scan/ScanStationPage';
import { displayStatus, listPending, replayAll } from '../../offline/outbox';
import { apiBase, authHeaders, resolveOrgId } from '../../utils/authHeaders';

const API = apiBase();

/**
 * /mobile/scan — the existing scan station inside the mobile shell.
 *
 * The station page is rendered, not copied: it already does the outbox enqueue,
 * the idempotent command id, the offline replay and the "pending is never shown as
 * accepted" rule. What this wrapper adds is the mobile framing the task asks for —
 * one-handed layout with the actions in the bottom third, a 56px scan button, an
 * optional vibration on success, and a queue panel that shows what the server said.
 *
 * The server's gate verdict is shown exactly as returned: `result` plus every
 * entry in `reasons`. Nothing here rewords or filters it.
 */

/** Success feedback. Off unless the caller asks for it; vibration is silent where absent. */
export function feedbackFor(result, { sound = false } = {}) {
  const accepted = result === 'accepted';
  if (!accepted) return { vibrate: null, sound: false };
  return {
    vibrate: typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function' ? [40] : null,
    sound,
  };
}

export function fireFeedback(result, options) {
  const plan = feedbackFor(result, options);
  if (plan.vibrate && typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(plan.vibrate);
  }
  return plan;
}

/** One row of the queue panel, in the words the server used. */
export function QueueRow({ row }) {
  const label = row.pending ? 'PENDING' : displayStatus(row) || row.result;
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm" data-testid={`mobile-scan-row-${row.command_id || row.id}`}>
      <span className="text-ink-muted">{row.code}</span>
      <span data-status={displayStatus(row)} data-testid={`mobile-scan-status-${row.command_id || row.id}`}>
        {label}
      </span>
      {Array.isArray(row.reasons) && row.reasons.length > 0 && (
        <span className="text-danger text-xs" data-testid={`mobile-scan-reasons-${row.command_id || row.id}`}>
          {row.reasons.join(', ')}
        </span>
      )}
    </li>
  );
}

export default function MobileScan({ soundEnabled = false }) {
  const [pending, setPending] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [replaying, setReplaying] = useState(false);

  const orgId = useMemo(() => resolveOrgId(), []);

  const refresh = useCallback(async () => {
    try {
      if (typeof indexedDB === 'undefined') return;
      setPending(await listPending());
    } catch {
      /* IndexedDB unavailable (private mode) */
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // The station rendered inside this wrapper queues scans itself, so the queue
  // panel here has to re-read rather than assume it owns the outbox. Focus and
  // online cover the common cases; the interval covers a scan taken while the
  // screen stays open.
  useEffect(() => {
    const onFocus = () => { refresh(); };
    const onOnline = () => { refresh(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    const timer = setInterval(refresh, 2000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      clearInterval(timer);
    };
  }, [refresh]);

  /** Replay the outbox; the station's own sender is not needed for the key check. */
  const flush = useCallback(async () => {
    setReplaying(true);
    try {
      await replayAll(async (row) => {
        const res = await fetch(`${API}/api/scan/commands`, {
          method: 'POST',
          headers: authHeaders({ orgId, json: true }),
          body: JSON.stringify({
            commands: [{
              command_id: row.command_id,
              payload_hash: row.payload_hash,
              gate: row.gate,
              action: row.action,
              code: row.code,
              qty: row.qty,
              uom: row.uom,
              ref: row.ref,
              captured_offline: true,
            }],
          }),
        });
        const body = await res.json().catch(() => ({}));
        const event = (body.events && body.events[0]) || null;
        if (event) setLastResult(event);
        return { status: res.status, body, httpStatus: res.status };
      });
      await refresh();
    } finally {
      setReplaying(false);
    }
  }, [orgId, refresh]);

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="mobile-scan">
      {/* The station itself, unchanged. */}
      <div className="pb-56">
        <ScanStationPage />
      </div>

      {lastResult && (
        <section className="px-4 py-3" data-testid="mobile-scan-last-result">
          <p className="text-sm text-ink">
            Gate {lastResult.gate}: <strong>{lastResult.result}</strong>
          </p>
          {Array.isArray(lastResult.reasons) && lastResult.reasons.length > 0 && (
            <p className="text-sm text-danger" data-testid="mobile-scan-last-reasons">
              {lastResult.reasons.join(', ')}
            </p>
          )}
        </section>
      )}

      {pending.length > 0 && (
        <section className="px-4 pb-3" data-testid="mobile-scan-queue">
          <h2 className="text-sm font-medium text-ink mb-2">Waiting to send</h2>
          <ul className="space-y-1">
            {pending.map((row) => <QueueRow key={row.command_id} row={row} />)}
          </ul>
        </section>
      )}

      {/* One-handed: the actions sit in the bottom third. */}
      <div
        className="fixed inset-x-0 bottom-0 z-30 bg-surface border-t border-surface-border px-4 pt-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
        data-testid="mobile-scan-actions"
      >
        <button
          type="button"
          className="w-full bg-primary-500 text-ink-inverse rounded font-medium"
          style={{ height: 56 }}
          data-testid="mobile-scan-primary"
          onClick={flush}
          disabled={replaying}
        >
          {pending.length > 0 ? `Send ${pending.length} scan${pending.length === 1 ? '' : 's'}` : 'Scan ready'}
        </button>
        <p className="text-xs text-ink-muted mt-2 text-center">
          {soundEnabled ? 'Sound on' : 'Sound off'} · {pending.length} queued
        </p>
      </div>
    </div>
  );
}

export { fireFeedback as _fireFeedback };
